import React, { useState, useEffect, useRef } from "react";
import Card from "components/card";
import ExpandableDescription from "components/fields/ExpandableDescription";
import { MdEdit, MdClose, MdDelete } from "react-icons/md";
import { FiPlus, FiSearch } from "react-icons/fi";
import { BsThreeDotsVertical } from "react-icons/bs";
import Switch from "components/switch";
import AudioRoomService from "services/audio-room.service";
import LivestreamService from "services/livestream.service";
import Toast from "components/toaster";
import Select from "react-select";
import Loader from "assets/img/loader.gif";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Module-level tabCache for persistent data across component mounts/unmounts
const tabCache = {
  "audio-room": {
    data: null,
    firstLoad: true,
  },
  "livestream": {
    data: null,
    firstLoad: true,
  },
};

// Non-Sortable Card Component (for both audio room and livestream - no drag and drop on cards)
const NonSortableCard = ({ item, index, itemId, menuDropdownRefs, openMenuId, setOpenMenuId, handleEditClick, handleDeleteClick }) => {
  return (
    <div className="h-full">
      <Card
        extra="w-full h-full p-4 md:p-5 shadow-sm hover:shadow-md transition-shadow !bg-[#192242] !rounded-xl"
      >
        <div className="flex flex-col h-full relative">
          <div className="flex-1">
            <div className="flex-1 space-y-4">
              {/* Package Name with Status Badge and Menu */}
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-[19px] font-bold text-white">
                    {item.package_name || "-"}
                  </h3>
                  {item.is_popular && (
                    <div className="flex items-center justify-center rounded-lg px-2 py-0.5 text-xs font-bold bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-400 border border-yellow-500/30">
                      <span>Popular</span>
                    </div>
                  )}
                </div>
                
                {/* Action Buttons - Right Side */}
                <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  {/* Status Badge */}
                  <div
                    className={`flex items-center justify-center rounded-lg px-3 py-1 text-xs font-bold ${
                      item.is_active
                        ? "bg-green-500/20 text-green-500"
                        : ""
                    }`}
                    style={!item.is_active ? {
                      backgroundColor: "rgba(255,37,37,0.1)",
                      color: "#f06670d5"
                    } : {}}
                  >
                    <span>{item.is_active ? "Active" : "Inactive"}</span>
                  </div>

                  {/* 3-Dot Menu Dropdown */}
                  <div 
                    className="relative"
                    ref={(el) => {
                      if (el) {
                        menuDropdownRefs.current[itemId] = el;
                      } else {
                        delete menuDropdownRefs.current[itemId];
                      }
                    }}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuId(openMenuId === itemId ? null : itemId);
                      }}
                      className="flex items-center justify-center h-7 w-7 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                      title="Menu"
                    >
                      <BsThreeDotsVertical className="h-4 w-4 text-white/90" />
                    </button>

                    {openMenuId === itemId && (
                      <div
                        className="absolute right-0 z-50 mt-1 w-28 rounded-md bg-[#0F1735] border border-gray-600 shadow-lg py-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(item);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-white/90 hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <MdEdit className="h-4 w-4" />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(item);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <MdDelete className="h-4 w-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {/* Duration with Discount Badge and Coins on same line */}
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-end gap-2 flex-wrap">
                    <span className="text-lg-*+/+9521 font-bold text-white">
                       {item.duration_minutes || "0"} Minutes 
                    </span>
                    {Number(item.discount_percentage) > 0 && (
                      <span className="inline-flex items-center justify-center rounded-lg px-2 py-0.5 text-xs font-bold bg-gradient-to-r from-red-500/20 to-orange-500/20 text-red-400 border border-red-500/30 mb-0.5">
                        {item.discount_percentage}% OFF 
                      </span>
                    )}
                  </div>
                  
                  {/* Coins Display */}
                  {Number(item.discount_percentage) > 0 ? (
                    <span
                      className="text-base 3xl:text-lg font-bold"
                      style={{
                        background: 'linear-gradient(216.93deg, #1DB4FF 18.65%, #E934F5 83.43%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      {Math.round((item.coins || 0) * (1 - (item.discount_percentage || 0) / 100))} Coins
                    </span>
                  ) : (
                    <span 
                      className="text-base 3xl:text-lg font-bold"
                      style={{
                        background: 'linear-gradient(216.93deg, #1DB4FF 18.65%, #E934F5 83.43%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      {item.coins || "0"} Coins
                    </span>
                  )}
                </div>

                {item.description && (
                  <>
                    <div className="-mx-1 my-1 border-t border-gray-800"></div>
                    
                    <div>
                      <ExpandableDescription 
                        text={item.description}
                        maxLines={2}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Sortable Card Component (for audio-room - with drag and drop)
const SortableCard = ({ item, index, itemId, uniqueId, menuDropdownRefs, openMenuId, setOpenMenuId, handleEditClick, handleDeleteClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: uniqueId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="h-full"
    >
      <Card
        extra="w-full h-full p-4 md:p-5 shadow-sm hover:shadow-md transition-shadow !bg-[#192242] !rounded-xl"
      >
        <div className="flex flex-col h-full relative">
          {/* Drag Handle Area */}
          <div 
            className="cursor-move flex-1"
            {...attributes}
            {...listeners}
            style={{ touchAction: 'none' }}
          >
            <div className="flex-1 space-y-4">
              {/* Package Name with Status Badge and Menu */}
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-[19px] font-bold text-white">
                    {item.package_name || "-"}
                  </h3>
                  {item.is_popular && (
                    <div className="flex items-center justify-center rounded-lg px-2 py-0.5 text-xs font-bold bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-400 border border-yellow-500/30">
                      <span>Popular</span>
                    </div>
                  )}
                </div>
                
                {/* Action Buttons - Right Side */}
                <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  {/* Status Badge */}
                  <div
                    className={`flex items-center justify-center rounded-lg px-3 py-1 text-xs font-bold ${
                      item.is_active
                        ? "bg-green-500/20 text-green-500"
                        : ""
                    }`}
                    style={!item.is_active ? {
                      backgroundColor: "rgba(255,37,37,0.1)",
                      color: "#f06670d5"
                    } : {}}
                  >
                    <span>{item.is_active ? "Active" : "Inactive"}</span>
                  </div>

                  {/* 3-Dot Menu Dropdown */}
                  <div 
                    className="relative"
                    ref={(el) => {
                      if (el) {
                        menuDropdownRefs.current[itemId] = el;
                      } else {
                        delete menuDropdownRefs.current[itemId];
                      }
                    }}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuId(openMenuId === itemId ? null : itemId);
                      }}
                      className="flex items-center justify-center h-7 w-7 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                      title="Menu"
                    >
                      <BsThreeDotsVertical className="h-4 w-4 text-white/90" />
                    </button>

                    {openMenuId === itemId && (
                      <div
                        className="absolute right-0 z-50 mt-1 w-28 rounded-md bg-[#0F1735] border border-gray-600 shadow-lg py-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(item);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-white/90 hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <MdEdit className="h-4 w-4" />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(item);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <MdDelete className="h-4 w-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {/* Duration with Discount Badge and Coins on same line */}
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-end gap-2 flex-wrap">
                    <span className="text-lg-*+/+9521 font-bold text-white">
                       {item.duration_minutes || "0"} Minutes 
                    </span>
                    {Number(item.discount_percentage) > 0 && (
                      <span className="inline-flex items-center justify-center rounded-lg px-2 py-0.5 text-xs font-bold bg-gradient-to-r from-red-500/20 to-orange-500/20 text-red-400 border border-red-500/30 mb-0.5">
                        {item.discount_percentage}% OFF 
                      </span>
                    )}
                  </div>
                  
                  {/* Coins Display */}
                  {Number(item.discount_percentage) > 0 ? (
                    <span
                      className="text-base 3xl:text-lg font-bold"
                      style={{
                        background: 'linear-gradient(216.93deg, #1DB4FF 18.65%, #E934F5 83.43%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      {Math.round((item.coins || 0) * (1 - (item.discount_percentage || 0) / 100))} Coins
                    </span>
                  ) : (
                    <span 
                      className="text-base 3xl:text-lg font-bold"
                      style={{
                        background: 'linear-gradient(216.93deg, #1DB4FF 18.65%, #E934F5 83.43%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      {item.coins || "0"} Coins
                    </span>
                  )}
                </div>

                {item.description && (
                  <>
                    <div className="-mx-1 my-1 border-t border-gray-800"></div>
                    
                    <div>
                      <ExpandableDescription 
                        text={item.description}
                        maxLines={2}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Sortable Audio Room Row Component (for modal reordering)
const SortableAudioRoomRow = ({ item, index, activeId, overId }) => {
  const uniqueId = item.id || item._id || item.package_id || `item-${index}`;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: uniqueId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isOver = overId === uniqueId && activeId !== uniqueId;
  const isDraggingItem = activeId === uniqueId;

  return (
    <>
      {/* Drag line indicator */}
      {isOver && (
        <div className="h-0.5 bg-blue-500 mb-2 mx-2 rounded-full" />
      )}
      <div
        ref={setNodeRef}
        style={{ ...style, touchAction: 'none' }}
        {...attributes}
        {...listeners}
        className={`cursor-move flex items-start gap-3 p-3 rounded-lg border border-gray-700 bg-[#192242] hover:bg-[#1a2748] transition-colors ${isDragging || isDraggingItem ? 'shadow-lg opacity-50' : ''}`}
      >
        {/* Order Number */}
        <div className="flex-shrink-0 w-6 text-center text-white/70 font-medium pt-0.5">
          {index + 1}
        </div>

        {/* Content Section */}
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          {/* First Line: Package Name */}
          <div className="min-w-0">
            <h3 className="text-base font-bold text-white truncate">
              {item.package_name || "-"}
            </h3>
          </div>

          {/* Second Line: Duration and Coins */}
          <div className="flex items-center gap-4">
            {/* Duration */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-white/70">Duration:</span>
              <span className="text-sm font-medium text-white">
                {item.duration_minutes || "0"} Minutes
              </span>
            </div>

            {/* Coins */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-white/70">Coins:</span>
              <span 
                className="text-sm font-bold"
                style={{
                  background: "linear-gradient(216.93deg, #1DB4FF 18.65%, #E934F5 83.43%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text"
                }}
              >
                {Number(item.discount_percentage) > 0 
                  ? Math.round((item.coins || 0) * (1 - (item.discount_percentage || 0) / 100))
                  : (item.coins || "0")} Coins
              </span>
            </div>
          </div>
        </div>

        {/* Drag Handle - 6-dot grip icon on the right center */}
        <div 
          className="flex-shrink-0 flex items-center justify-center text-white/50 self-center pointer-events-none"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
          >
            {/* Top row - 2 dots */}
            <circle cx="6" cy="5" r="1.5" fill="currentColor" opacity="0.6" />
            <circle cx="14" cy="5" r="1.5" fill="currentColor" opacity="0.6" />
            {/* Middle row - 2 dots */}
            <circle cx="6" cy="10" r="1.5" fill="currentColor" opacity="0.6" />
            <circle cx="14" cy="10" r="1.5" fill="currentColor" opacity="0.6" />
            {/* Bottom row - 2 dots */}
            <circle cx="6" cy="15" r="1.5" fill="currentColor" opacity="0.6" />
            <circle cx="14" cy="15" r="1.5" fill="currentColor" opacity="0.6" />
          </svg>
        </div>
      </div>
    </>
  );
};

// Sortable Livestream Row Component (for modal reordering)
const SortableLivestreamRow = ({ item, index, activeId, overId }) => {
  const uniqueId = item.id || item._id || item.package_id || `item-${index}`;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: uniqueId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isOver = overId === uniqueId && activeId !== uniqueId;
  const isDraggingItem = activeId === uniqueId;

  return (
    <>
      {/* Drag line indicator */}
      {isOver && (
        <div className="h-0.5 bg-blue-500 mb-2 mx-2 rounded-full" />
      )}
      <div
        ref={setNodeRef}
        style={{ ...style, touchAction: 'none' }}
        {...attributes}
        {...listeners}
        className={`cursor-move flex items-start gap-3 p-3 rounded-lg border border-gray-700 bg-[#192242] hover:bg-[#1a2748] transition-colors ${isDragging || isDraggingItem ? 'shadow-lg opacity-50' : ''}`}
      >
        {/* Order Number */}
        <div className="flex-shrink-0 w-6 text-center text-white/70 font-medium pt-0.5">
          {index + 1}
        </div>

        {/* Content Section */}
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          {/* First Line: Package Name */}
          <div className="min-w-0">
            <h3 className="text-base font-bold text-white truncate">
              {item.package_name || "-"}
            </h3>
          </div>

          {/* Second Line: Duration and Coins */}
          <div className="flex items-center gap-4">
            {/* Duration */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-white/70">Duration:</span>
              <span className="text-sm font-medium text-white">
                {item.duration_minutes || "0"} Minutes
              </span>
            </div>

            {/* Coins */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-white/70">Coins:</span>
              <span 
                className="text-sm font-bold"
                style={{
                  background: "linear-gradient(216.93deg, #1DB4FF 18.65%, #E934F5 83.43%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text"
                }}
              >
                {Number(item.discount_percentage) > 0 
                  ? Math.round((item.coins || 0) * (1 - (item.discount_percentage || 0) / 100))
                  : (item.coins || "0")} Coins
              </span>
            </div>
          </div>
        </div>

        {/* Drag Handle - 6-dot grip icon on the right center */}
        <div 
          className="flex-shrink-0 flex items-center justify-center text-white/50 self-center pointer-events-none"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
          >
            {/* Top row - 2 dots */}
            <circle cx="6" cy="5" r="1.5" fill="currentColor" opacity="0.6" />
            <circle cx="14" cy="5" r="1.5" fill="currentColor" opacity="0.6" />
            {/* Middle row - 2 dots */}
            <circle cx="6" cy="10" r="1.5" fill="currentColor" opacity="0.6" />
            <circle cx="14" cy="10" r="1.5" fill="currentColor" opacity="0.6" />
            {/* Bottom row - 2 dots */}
            <circle cx="6" cy="15" r="1.5" fill="currentColor" opacity="0.6" />
            <circle cx="14" cy="15" r="1.5" fill="currentColor" opacity="0.6" />
          </svg>
        </div>
      </div>
    </>
  );
};

const AudioRoom = () => {
  // View selection state
  const [activeView, setActiveView] = useState(() => {
    return localStorage.getItem("audio_room_active_view") || "audio-room";
  });


  // State for audio room packages data
  const [audioRoomData, setAudioRoomData] = useState([]);
  
  // State for live stream packages data
  const [livestreamData, setLivestreamData] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [isFetching, setIsFetching] = useState(false);

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({
    package_name: "",
    coins: "",
    discount_percentage: "",
    duration_minutes: "",
    description: "",
    is_active: true,
    is_popular: false,
  });
  const [editFormErrors, setEditFormErrors] = useState({});
  
  // Add modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addFormData, setAddFormData] = useState({
    package_name: "",
    coins: "",
    discount_percentage: "",
    duration_minutes: "",
    description: "",
    is_active: true,
    is_popular: false,
  });
  const [addFormErrors, setAddFormErrors] = useState({});
  
  const [isLoading, setIsLoading] = useState(false);
  
  // Menu dropdown state
  const menuDropdownRefs = useRef({});
  const [openMenuId, setOpenMenuId] = useState(null);
  
  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Audio Room Edit Order modal state
  const [isAudioRoomEditOrderModalOpen, setIsAudioRoomEditOrderModalOpen] = useState(false);
  const [reorderAudioRoomData, setReorderAudioRoomData] = useState([]);
  const [isSavingAudioRoomOrder, setIsSavingAudioRoomOrder] = useState(false);

  // Livestream Edit Order modal state
  const [isLivestreamEditOrderModalOpen, setIsLivestreamEditOrderModalOpen] = useState(false);
  const [reorderLivestreamData, setReorderLivestreamData] = useState([]);
  const [isSavingLivestreamOrder, setIsSavingLivestreamOrder] = useState(false);
  
  // State for tracking drag over item in reorder modal
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  
  // State for tracking drag over item in audio room reorder modal
  const [audioRoomActiveId, setAudioRoomActiveId] = useState(null);
  const [audioRoomOverId, setAudioRoomOverId] = useState(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before activating
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // React-select styles
  const colourStyles = {
    control: (styles) => ({
      ...styles,
      minHeight: "40px",
      height: "40px",
      backgroundColor: "#0b1437",
      border: "none",
      boxShadow: "none",
      "&:hover": {
        border: "none",
      },
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      return {
        ...styles,
        backgroundColor: isDisabled
          ? undefined
          : isSelected
            ? "#24388a"
            : "#0b1437",
        color: isDisabled ? "#ccc" : "white",
        cursor: isDisabled ? "not-allowed" : "default",
        ":active": {
          ...styles[":active"],
          backgroundColor: !isDisabled
            ? isSelected
              ? "#24388a"
              : "#1a2d6b"
            : undefined,
        },
        ":hover": {
          backgroundColor: isSelected ? "#24388a" : "#1a2d6b",
          color: "white",
        },
      };
    },
    menu: (styles) => ({
      ...styles,
      backgroundColor: "#0b1437",
      borderRadius: "0.75rem",
      overflow: "hidden",
    }),
    menuList: (styles) => ({
      ...styles,
      backgroundColor: "#0b1437",
      padding: 0,
    }),
    singleValue: (styles) => ({
      ...styles,
      color: "white",
    }),
    input: (styles) => ({
      ...styles,
      color: "white",
    }),
  };

  // Fetch audio room packages
  const fetchAudioRoomPackages = async (showLoader = true) => {
    if (showLoader) {
    setIsFetching(true);
    }
    try {
      const body = {
        pagination: {
          page: currentPage,
          pageSize: pageSize,
          sort: {
            display_order: 1,
            createdAt: -1,
          },
          filters: {},
        },
        searchtext: searchText,
      };
      const response = await AudioRoomService.getAudioRoomPackages(body);
      const responseData = response.data.data || [];
      const responseTotalCount = response.data.totalCount || 0;
      
      // Update cache
      tabCache["audio-room"].data = {
        data: responseData,
        totalCount: responseTotalCount,
      };
      
      // Set firstLoad to false after successful fetch
      if (tabCache["audio-room"].firstLoad) {
        tabCache["audio-room"].firstLoad = false;
      }
      
      setAudioRoomData(responseData);
      setTotalCount(responseTotalCount);
    } catch (error) {
      console.error("Error fetching audio room packages:", error);
      Toast("error", error.response?.data?.message || "Failed to fetch audio room packages");
    } finally {
      if (showLoader) {
      setIsFetching(false);
      }
    }
  };

  // Fetch live stream packages
  const fetchLivestreamPackages = async (showLoader = true) => {
    if (showLoader) {
    setIsFetching(true);
    }
    try {
      const body = {
        pagination: {
          page: currentPage,
          pageSize: pageSize,
          sort: {
            display_order: 1,
            createdAt: -1,
          },
          filters: {},
        },
        searchtext: searchText,
      };
      
      const response = await LivestreamService.getLivestreamPackages(body);
      const responseData = response.data.data || [];
      const responseTotalCount = response.data.totalCount || 0;
      
      // Update cache
      tabCache["livestream"].data = {
        data: responseData,
        totalCount: responseTotalCount,
      };
      
      // Set firstLoad to false after successful fetch
      if (tabCache["livestream"].firstLoad) {
        tabCache["livestream"].firstLoad = false;
      }
      
      setLivestreamData(responseData);
      setTotalCount(responseTotalCount);
    } catch (error) {
      console.error("Error fetching live stream packages:", error);
      Toast("error", error.response?.data?.message || "Failed to fetch live stream packages");
    } finally {
      if (showLoader) {
      setIsFetching(false);
      }
    }
  };

  useEffect(() => {
    if (activeView) {
      localStorage.setItem("audio_room_active_view", activeView);
    }
  }, [activeView]);
  

  useEffect(() => {
    if (activeView === "audio-room") {
      // Check cache first
      const cache = tabCache["audio-room"];
      
      // If cached data exists, show it instantly
      if (cache.data) {
        setAudioRoomData(cache.data.data || []);
        setTotalCount(cache.data.totalCount || 0);
      }
      
      // Always fetch fresh data in background
      // Show loader only on firstLoad (when no cache exists yet)
      const shouldShowLoader = cache.firstLoad && !cache.data;
      fetchAudioRoomPackages(shouldShowLoader);
    } else {
      // Check cache first
      const cache = tabCache["livestream"];
      
      // If cached data exists, show it instantly
      if (cache.data) {
        setLivestreamData(cache.data.data || []);
        setTotalCount(cache.data.totalCount || 0);
      }
      
      // Always fetch fresh data in background
      // Show loader only on firstLoad (when no cache exists yet)
      const shouldShowLoader = cache.firstLoad && !cache.data;
      fetchLivestreamPackages(shouldShowLoader);
    }
  }, [currentPage, pageSize, searchText, activeView]);

  // Helper function to get unique ID for an item
  const getItemId = (item, index) => {
    return item.id || item._id || item.package_id || `item-${index}`;
  };

  // Toggle menu for a specific card
  const toggleMenu = (id) => {
    setOpenMenuId(openMenuId === id ? null : id);
  };

  // Handle clicks outside menu dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside any menu dropdown
      const clickedInsideMenu = Object.values(menuDropdownRefs.current).some(
        (ref) => ref && ref.contains(event.target)
      );
      if (!clickedInsideMenu) {
        setOpenMenuId(null);
      }
    };

    // Use mousedown but with a small delay to let button onClick complete
    const handleMouseDown = (event) => {
      setTimeout(() => {
        handleClickOutside(event);
      }, 0);
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, []);

  const handleEditClick = (item) => {
    setEditingItem(item);
    setEditFormData({
      package_name: item.package_name || "",
      coins: item.coins || "",
      discount_percentage: item.discount_percentage !== undefined ? item.discount_percentage : "",
      duration_minutes: item.duration_minutes || "",
      description: item.description || "",
      is_active: item.is_active !== undefined ? item.is_active : true,
      is_popular: item.is_popular !== undefined ? item.is_popular : false,
    });
    setEditFormErrors({});
    setIsEditModalOpen(true);
    setOpenMenuId(null);
  };

  const handleDeleteClick = (item) => {
    setDeletingItem(item);
    setIsDeleteModalOpen(true);
    setOpenMenuId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingItem) return;

    setIsDeleting(true);
    try {
      const packageId = deletingItem._id || deletingItem.id || deletingItem.package_id;
      const payload = {
        package_id: packageId,
      };

      if (activeView === "audio-room") {
        await AudioRoomService.deleteAudioRoomPackage(payload);
        await fetchAudioRoomPackages(false);
        Toast("success", "Audio room package deleted successfully");
      } else {
        await LivestreamService.deleteLivestreamPackage(payload);
        await fetchLivestreamPackages(false);
        Toast("success", "Live stream package deleted successfully");
      }
      
      setIsDeleteModalOpen(false);
      setDeletingItem(null);
    } catch (error) {
      console.error(`Error deleting ${activeView === "audio-room" ? "audio room" : "live stream"} package:`, error);
      Toast("error", error.response?.data?.message || `Failed to delete ${activeView === "audio-room" ? "audio room" : "live stream"} package`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingItem(null);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setEditingItem(null);
    setEditFormData({
      package_name: "",
      coins: "",
      discount_percentage: "",
      duration_minutes: "",
      description: "",
      is_active: true,
      is_popular: false,
    });
    setEditFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    
    if (!editFormData.package_name.trim()) {
      errors.package_name = "Package name is required";
    }
    
    if (!editFormData.coins || editFormData.coins <= 0) {
      errors.coins = "Coins must be greater than 0";
    }
    
    if (!editFormData.duration_minutes || editFormData.duration_minutes <= 0) {
      errors.duration_minutes = "Duration must be greater than 0";
    }
    
    if (!editFormData.description.trim()) {
      errors.description = "Description is required";
    }
    
    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveChanges = async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const updateData = {
        package_id: editingItem._id || editingItem.id,
        package_name: editFormData.package_name,
        coins: Number(editFormData.coins),
        discount_percentage: editFormData.discount_percentage !== "" ? Number(editFormData.discount_percentage) :0,
        duration_minutes: Number(editFormData.duration_minutes),
        description: editFormData.description,
        is_active: editFormData.is_active,
        is_popular: editFormData.is_popular,
        display_order: editingItem.display_order !== undefined ? Number(editingItem.display_order) : 1,
      };
      
      if (activeView === "audio-room") {
        await AudioRoomService.updateAudioRoomPackage(updateData);
        await fetchAudioRoomPackages(false);
        Toast("success", "Audio room package updated successfully");
      } else {
        await LivestreamService.updateLivestreamPackage(updateData);
        await fetchLivestreamPackages(false);
        Toast("success", "Live stream package updated successfully");
      }
      
      handleCloseModal();
    } catch (error) {
      console.error(`Error updating ${activeView === "audio-room" ? "audio room" : "live stream"} package:`, error);
      Toast("error", error.response?.data?.message || `Failed to update ${activeView === "audio-room" ? "audio room" : "live stream"} package`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddClick = () => {
    setAddFormData({
      package_name: "",
      coins: "",
      discount_percentage: "",
      duration_minutes: "",
      description: "",
      is_active: true,
      is_popular: false,
    });
    setAddFormErrors({});
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setAddFormData({
      package_name: "",
      coins: "",
      discount_percentage: "",
      duration_minutes: "",
      description: "",
      is_active: true,
      is_popular: false,
    });
    setAddFormErrors({});
  };

  const validateAddForm = () => {
    const errors = {};
    
    if (!addFormData.package_name.trim()) {
      errors.package_name = "Package name is required";
    }
    
    if (!addFormData.coins || addFormData.coins <= 0) {
      errors.coins = "Coins must be greater than 0";
    }
    
    if (!addFormData.duration_minutes || addFormData.duration_minutes <= 0) {
      errors.duration_minutes = "Duration must be greater than 0";
    }
    
    if (!addFormData.description.trim()) {
      errors.description = "Description is required";
    }
    
    setAddFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddPackage = async () => {
    if (!validateAddForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const createData = {
        package_name: addFormData.package_name,
        coins: Number(addFormData.coins),
        discount_percentage: addFormData.discount_percentage !== "" ? Number(addFormData.discount_percentage) : 0,
        duration_minutes: Number(addFormData.duration_minutes),
        description: addFormData.description,
        is_active: addFormData.is_active,
        is_popular: addFormData.is_popular,
        display_order: 1,
      };
      
      if (activeView === "audio-room") {
        await AudioRoomService.createAudioRoomPackage(createData);
        await fetchAudioRoomPackages(false);
        Toast("success", "Audio room package created successfully");
      } else {
        await LivestreamService.createLivestreamPackage(createData);
        await fetchLivestreamPackages(false);
        Toast("success", "Live stream package created successfully");
      }
      
      handleCloseAddModal();
    } catch (error) {
      console.error(`Error creating ${activeView === "audio-room" ? "audio room" : "live stream"} package:`, error);
      Toast("error", error.response?.data?.message || `Failed to create ${activeView === "audio-room" ? "audio room" : "live stream"} package`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handler for opening Audio Room Edit Order modal
  const handleAudioRoomEditOrderClick = () => {
    // Create a copy of all audio room packages for reordering
    const itemsWithIds = audioRoomData.map((item, index) => ({
      ...item,
      id: item.id || item._id || item.package_id || `item-${index}`,
    }));
    setReorderAudioRoomData(itemsWithIds);
    setIsAudioRoomEditOrderModalOpen(true);
  };

  // Handler for closing Audio Room Edit Order modal
  const handleCloseAudioRoomEditOrderModal = () => {
    setIsAudioRoomEditOrderModalOpen(false);
    setReorderAudioRoomData([]);
    setAudioRoomActiveId(null);
    setAudioRoomOverId(null);
  };

  // Handler for drag start in Audio Room Edit Order modal
  const handleAudioRoomReorderDragStart = (event) => {
    setAudioRoomActiveId(event.active.id);
  };

  // Handler for drag over in Audio Room Edit Order modal
  const handleAudioRoomReorderDragOver = (event) => {
    setAudioRoomOverId(event.over?.id || null);
  };

  // Handler for drag end in Audio Room Edit Order modal
  const handleAudioRoomReorderDragEnd = (event) => {
    const { active, over } = event;

    setAudioRoomActiveId(null);
    setAudioRoomOverId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = reorderAudioRoomData.findIndex((item) => {
      const uniqueId = item.id || item._id || item.package_id;
      return uniqueId === active.id;
    });
    const newIndex = reorderAudioRoomData.findIndex((item) => {
      const uniqueId = item.id || item._id || item.package_id;
      return uniqueId === over.id;
    });

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Update local state for reordering
    const newData = arrayMove(reorderAudioRoomData, oldIndex, newIndex);
    setReorderAudioRoomData(newData);
  };

  // Handler for saving the new audio room order
  const handleSaveAudioRoomOrder = async () => {
    setIsSavingAudioRoomOrder(true);
    try {
      // Update display_order values for all items
      const updatedData = reorderAudioRoomData.map((item, index) => ({
        ...item,
        display_order: index,
      }));

      // Update order for all audio room packages using existing updateAudioRoomPackage API
      const updatePromises = updatedData.map((item, index) => {
        const packageId = item._id || item.id || item.package_id;
        return AudioRoomService.updateAudioRoomPackage({
          package_id: packageId,
          package_name: item.package_name,
          coins: Number(item.coins),
          discount_percentage: item.discount_percentage !== undefined ? Number(item.discount_percentage) : 0,
          duration_minutes: Number(item.duration_minutes),
          description: item.description,
          is_active: item.is_active !== undefined ? item.is_active : true,
          is_popular: item.is_popular !== undefined ? item.is_popular : false,
          display_order: index,
        });
      });

      // Wait for all updates to complete
      await Promise.all(updatePromises);
      
      // Update main data and cache
      setAudioRoomData(updatedData);
      tabCache["audio-room"].data = {
        data: updatedData,
        totalCount: totalCount,
      };
      
      Toast("success", "Audio room packages reordered successfully");
      handleCloseAudioRoomEditOrderModal();
    } catch (error) {
      console.error("Error reordering audio room packages:", error);
      Toast("error", error.response?.data?.message || "Failed to reorder audio room packages");
    } finally {
      setIsSavingAudioRoomOrder(false);
    }
  };

  // Handler for opening Livestream Edit Order modal
  const handleLivestreamEditOrderClick = () => {
    // Create a copy of all livestream packages for reordering
    const itemsWithIds = livestreamData.map((item, index) => ({
      ...item,
      id: item.id || item._id || item.package_id || `item-${index}`,
    }));
    setReorderLivestreamData(itemsWithIds);
    setIsLivestreamEditOrderModalOpen(true);
  };

  // Handler for closing Livestream Edit Order modal
  const handleCloseLivestreamEditOrderModal = () => {
    setIsLivestreamEditOrderModalOpen(false);
    setReorderLivestreamData([]);
    setActiveId(null);
    setOverId(null);
  };

  // Handler for drag start in Livestream Edit Order modal
  const handleLivestreamReorderDragStart = (event) => {
    setActiveId(event.active.id);
  };

  // Handler for drag over in Livestream Edit Order modal
  const handleLivestreamReorderDragOver = (event) => {
    setOverId(event.over?.id || null);
  };

  // Handler for drag end in Livestream Edit Order modal
  const handleLivestreamReorderDragEnd = (event) => {
    const { active, over } = event;

    setActiveId(null);
    setOverId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = reorderLivestreamData.findIndex((item) => {
      const uniqueId = item.id || item._id || item.package_id;
      return uniqueId === active.id;
    });
    const newIndex = reorderLivestreamData.findIndex((item) => {
      const uniqueId = item.id || item._id || item.package_id;
      return uniqueId === over.id;
    });

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Update local state for reordering
    const newData = arrayMove(reorderLivestreamData, oldIndex, newIndex);
    setReorderLivestreamData(newData);
  };

  // Handler for saving the new livestream order
  const handleSaveLivestreamOrder = async () => {
    setIsSavingLivestreamOrder(true);
    try {
    // Update display_order values for all items
      const updatedData = reorderLivestreamData.map((item, index) => ({
      ...item,
      display_order: index,
    }));

    // Update order for all livestream packages using existing updateLivestreamPackage API
      const updatePromises = updatedData.map((item, index) => {
        const packageId = item._id || item.id || item.package_id;
        return LivestreamService.updateLivestreamPackage({
          package_id: packageId,
          package_name: item.package_name,
          coins: Number(item.coins),
          discount_percentage: item.discount_percentage !== undefined ? Number(item.discount_percentage) : 0,
          duration_minutes: Number(item.duration_minutes),
          description: item.description,
          is_active: item.is_active !== undefined ? item.is_active : true,
          is_popular: item.is_popular !== undefined ? item.is_popular : false,
          display_order: index,
        });
      });

      // Wait for all updates to complete
      await Promise.all(updatePromises);
      
      // Update main data and cache
      setLivestreamData(updatedData);
      tabCache["livestream"].data = {
        data: updatedData,
        totalCount: totalCount,
      };
      
      Toast("success", "Live stream packages reordered successfully");
      handleCloseLivestreamEditOrderModal();
    } catch (error) {
      console.error("Error reordering live stream packages:", error);
      Toast("error", error.response?.data?.message || "Failed to reorder live stream packages");
    } finally {
      setIsSavingLivestreamOrder(false);
    }
  };

  // Get current data based on active view
  const currentData = activeView === "audio-room" ? audioRoomData : livestreamData;
  const viewType = activeView === "audio-room" ? "Audio Room" : "Live Stream";
  const addButtonText = activeView === "audio-room" ? "Add Audio Plan" : "Add Stream Plan";

  return (
    <div>
      <style>{`
        .reorder-modal-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(156, 163, 175, 0.15) transparent;
          -webkit-overflow-scrolling: touch;
        }
        .reorder-modal-scroll:hover {
          scrollbar-color: rgba(156, 163, 175, 0.3) transparent;
        }
        .reorder-modal-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .reorder-modal-scroll::-webkit-scrollbar-button {
          display: none !important;
          height: 0 !important;
          width: 0 !important;
        }
        .reorder-modal-scroll::-webkit-scrollbar-button:start:decrement,
        .reorder-modal-scroll::-webkit-scrollbar-button:end:increment,
        .reorder-modal-scroll::-webkit-scrollbar-button:vertical:start:decrement,
        .reorder-modal-scroll::-webkit-scrollbar-button:vertical:end:increment {
          display: none !important;
          height: 0 !important;
          width: 0 !important;
        }
        .reorder-modal-scroll::-webkit-scrollbar-corner {
          display: none !important;
          background: transparent !important;
        }
        .reorder-modal-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .reorder-modal-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(156, 163, 175, 0.15);
          border-radius: 3px;
          transition: background-color 0.2s ease;
        }
        .reorder-modal-scroll:hover::-webkit-scrollbar-thumb {
          background-color: rgba(156, 163, 175, 0.3);
        }
        .reorder-modal-scroll::-webkit-scrollbar-thumb:hover {
          background-color: rgba(156, 163, 175, 0.5);
        }
        .reorder-modal-scroll::-webkit-scrollbar-thumb:active {
          background-color: rgba(156, 163, 175, 0.6);
        }
      `}</style>
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 mt-4 gap-3 md:gap-0">
        <div className="mt-2. md:mt-3 flex h-fit justify-start">
          <div className="flex items-center rounded-full bg-navy-800 py-2 h-[40px] text-white xl:w-[170px]">
            <p className="pl-3 pr-2 text-xl">
              <FiSearch className="h-4 w-4 text-white" />
            </p>
            <input
              type="text"
              placeholder="Search..."
              className="block w-[90px] max-w-[90px] bg-navy-800 text-sm font-medium text-white outline-none placeholder:!text-white"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1); // Reset to first page when searching
              }}
            />
          </div>
        </div>
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
          {/* Dropdown Selector */}
          <div className="mt-0 md:mt-1 flex h-fit items-center">
            <Select
              className="h-full w-full md:w-[200px] py-1 !text-white"
              classNamePrefix="select"
              value={[
                { label: "Audio Room Plans", value: "audio-room" },
                { label: "Live Stream Plans", value: "livestream" }
              ].find(option => option.value === activeView)}
              name="view-selector"
              options={[
                { label: "Audio Room Plans", value: "audio-room" },
                { label: "Live Stream Plans", value: "livestream" }
              ]}
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.label}
              isOptionSelected={(option) => option.value === activeView}
              onChange={(option) => {
                setActiveView(option.value);
                setCurrentPage(1);
                setSearchText("");
              }}
              styles={colourStyles}
            />
          </div>
          {activeView === "audio-room" && (
            <button
              onClick={handleAudioRoomEditOrderClick}
              className="flex items-center justify-center gap-2 rounded-full bg-primary-gradient px-4 py-2.5 text-sm font-medium text-white/90 transition duration-200 hover:bg-gray-700 w-full md:w-auto"
            >
              Edit Order
            </button>
          )}
          {activeView === "livestream" && (
            <button
              onClick={handleLivestreamEditOrderClick}
              className="flex items-center justify-center gap-2 rounded-full bg-primary-gradient px-4 py-2.5 text-sm font-medium text-white/90 transition duration-200 hover:bg-gray-700 w-full md:w-auto"
            >
              Edit Order
            </button>
          )}
          <button
            onClick={handleAddClick}
            className="flex items-center justify-center gap-2 rounded-full bg-primary-gradient px-4 py-2 text-sm font-medium text-white/90 transition duration-200 hover:opacity-90 w-full md:w-auto"
          >
            <FiPlus className="h-4 w-4" />
            {addButtonText}
          </button>
        </div>
      </div>
      <div className="mt-1 relative">
        {isFetching && tabCache[activeView]?.firstLoad && !tabCache[activeView]?.data ? (
          <div 
            className="fixed" 
            style={{ 
              top: '50vh', 
              left: '57vw', 
              transform: 'translate(-50%, -50%)', 
              zIndex: 40,
              margin: 0,
              padding: 0,
              width: 'auto',
              height: 'auto'
            }}
          >
            <img
              src={Loader}
              alt="Loading..."
              className="h-16 w-16 block"
              style={{ margin: '0 auto', padding: 0, display: 'block' }}
            />
          </div>
        ) : currentData.length === 0 ? (
          <div className="py-8 text-center text-white">
            No {viewType.toLowerCase()} packages found
          </div>
        ) : (
          <div className="mt-3 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 3xl:grid-cols-4 items-stretch">
            {currentData.map((item, index) => {
              const itemId = getItemId(item, index);
              return (
                <NonSortableCard
                  key={itemId}
                  item={item}
                  index={index}
                  itemId={itemId}
                  menuDropdownRefs={menuDropdownRefs}
                  openMenuId={openMenuId}
                  setOpenMenuId={setOpenMenuId}
                  handleEditClick={handleEditClick}
                  handleDeleteClick={handleDeleteClick}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fade-in px-4"
          onClick={handleCloseModal}
        >
          <div
            className="w-full max-w-md rounded-xl bg-navy-800 p-6 shadow-lg animate-fade-in modal-scrollable"
            style={{ maxHeight: '90vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Edit {viewType} Package</h2>
              <button
                onClick={handleCloseModal}
                className="flex items-center justify-center h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
              >
                <MdClose className="h-5 w-5 text-white/90" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Package Name */}
              <div>
                <label className="mb-2 block text-sm font-medium text-white/90">
                  Package Name
                </label>
                <input
                  type="text"
                  value={editFormData.package_name}
                  onChange={(e) => {
                    setEditFormData({
                      ...editFormData,
                      package_name: e.target.value,
                    });
                    if (editFormErrors.package_name) {
                      setEditFormErrors({
                        ...editFormErrors,
                        package_name: "",
                      });
                    }
                  }}
                  className={`w-full rounded-md border ${
                    editFormErrors.package_name
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                  } bg-navy-700 px-3 py-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400`}
                  placeholder="e.g., 10 Minutes Package"
                />
                {editFormErrors.package_name && (
                  <p className="mt-1 text-sm text-red-400">
                    {editFormErrors.package_name}
                  </p>
                )}
              </div>

              {/* Coins */}
              <div>
                <label className="mb-2 block text-sm font-medium text-white/90">
                  Coins
                </label>
                <input
                  type="number"
                  value={editFormData.coins}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEditFormData({
                      ...editFormData,
                      coins: value,
                    });
                    if (editFormErrors.coins) {
                      setEditFormErrors({
                        ...editFormErrors,
                        coins: "",
                      });
                    }
                  }}
                  className={`w-full rounded-md border ${
                    editFormErrors.coins
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                  } bg-navy-700 px-3 py-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400`}
                  placeholder="e.g., 100"
                  min="1"
                />
                {editFormErrors.coins && (
                  <p className="mt-1 text-sm text-red-400">
                    {editFormErrors.coins}
                  </p>
                )}
              </div>

              {/* Discount Percentage */}
              <div>
                <label className="mb-2 block text-sm font-medium text-white/90">
                  Discount Percentage
                </label>
                <input
                  type="number"
                  value={editFormData.discount_percentage}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEditFormData({
                      ...editFormData,
                      discount_percentage: value,
                    });
                    if (editFormErrors.discount_percentage) {
                      setEditFormErrors({
                        ...editFormErrors,
                        discount_percentage: "",
                      });
                    }
                  }}
                  className={`w-full rounded-md border ${
                    editFormErrors.discount_percentage
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                  } bg-navy-700 px-3 py-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400`}
                  placeholder="Add discount"
                  min="0"
                  max="100"
                />
                {editFormErrors.discount_percentage && (
                  <p className="mt-1 text-sm text-red-400">
                    {editFormErrors.discount_percentage}
                  </p>
                )}
              </div>

              {/* Duration Minutes */}
              <div>
                <label className="mb-2 block text-sm font-medium text-white/90">
                  Duration (Minutes)
                </label>
                <input
                  type="number"
                  value={editFormData.duration_minutes}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEditFormData({
                      ...editFormData,
                      duration_minutes: value,
                    });
                    if (editFormErrors.duration_minutes) {
                      setEditFormErrors({
                        ...editFormErrors,
                        duration_minutes: "",
                      });
                    }
                  }}
                  className={`w-full rounded-md border ${
                    editFormErrors.duration_minutes
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                  } bg-navy-700 px-3 py-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400`}
                  placeholder="e.g., 10"
                  min="1"
                />
                {editFormErrors.duration_minutes && (
                  <p className="mt-1 text-sm text-red-400">
                    {editFormErrors.duration_minutes}
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="mb-2 block text-sm font-medium text-white/90">
                  Description
                </label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => {
                    setEditFormData({
                      ...editFormData,
                      description: e.target.value,
                    });
                    if (editFormErrors.description) {
                      setEditFormErrors({
                        ...editFormErrors,
                        description: "",
                      });
                    }
                  }}
                  rows={2}
                  className={`w-full rounded-md border ${
                    editFormErrors.description
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                  } bg-navy-700 px-3 pt-2 pb-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 resize-none`}
                  placeholder="Enter package description"
                />
                {editFormErrors.description && (
                  <p className="mt-1 text-sm text-red-400">
                    {editFormErrors.description}
                  </p>
                )}
              </div>

              {/* Active Status */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-white">
                  Active Status
                </label>
                <Switch
                  checked={editFormData.is_active}
                  onChange={(e) => {
                    setEditFormData({
                      ...editFormData,
                      is_active: e.target.checked,
                    });
                  }}
                  color="purple"
                />
              </div>

              {/* Popular Status */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-white">
                  Popular
                </label>
                <Switch
                  checked={editFormData.is_popular}
                  onChange={(e) => {
                    setEditFormData({
                      ...editFormData,
                      is_popular: e.target.checked,
                    });
                  }}
                  color="purple"
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={handleCloseModal}
                disabled={isLoading}
                className="rounded-md bg-gray-600 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveChanges}
                disabled={isLoading}
                className="rounded-md bg-primary-gradient px-4 py-2 text-sm font-medium text-white/90 hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {isAddModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fade-in px-4"
          onClick={handleCloseAddModal}
        >
          <div
            className="w-full max-w-md rounded-xl bg-navy-800 p-6 shadow-lg animate-fade-in relative modal-scrollable"
            style={{ maxHeight: '90vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
              <button
                onClick={handleCloseAddModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-white/90 transition-colors"
              title="Close"
              >
              <MdClose className="h-6 w-6" />
              </button>
            
            <h2 className="mb-4 text-xl font-bold text-white/90 pr-8">Add {activeView === "audio-room" ? "Audio Plan" : "Stream Plan"}</h2>
            
            <div className="space-y-4">
              {/* Package Name */}
              <div>
                <label className="mb-2 block text-sm font-medium text-white/90">
                  Package Name
                </label>
                <input
                  type="text"
                  value={addFormData.package_name}
                  onChange={(e) => {
                    setAddFormData({
                      ...addFormData,
                      package_name: e.target.value,
                    });
                    if (addFormErrors.package_name) {
                      setAddFormErrors({
                        ...addFormErrors,
                        package_name: "",
                      });
                    }
                  }}
                  className={`w-full rounded-md border ${
                    addFormErrors.package_name
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                  } bg-navy-700 px-3 py-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400`}
                  placeholder="e.g., 10 Minutes Package"
                />
                {addFormErrors.package_name && (
                  <p className="mt-1 text-sm text-red-400">
                    {addFormErrors.package_name}
                  </p>
                )}
              </div>

              {/* Coins */}
              <div>
                <label className="mb-2 block text-sm font-medium text-white/90">
                  Coins
                </label>
                <input
                  type="number"
                  value={addFormData.coins}
                  onChange={(e) => {
                    const value = e.target.value;
                    setAddFormData({
                      ...addFormData,
                      coins: value,
                    });
                    if (addFormErrors.coins) {
                      setAddFormErrors({
                        ...addFormErrors,
                        coins: "",
                      });
                    }
                  }}
                  className={`w-full rounded-md border ${
                    addFormErrors.coins
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                  } bg-navy-700 px-3 py-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400`}
                  placeholder="Add coin"
                  min="1"
                />
                {addFormErrors.coins && (
                  <p className="mt-1 text-sm text-red-400">
                    {addFormErrors.coins}
                  </p>
                )}
              </div>

              {/* Discount Percentage */}
              <div>
                <label className="mb-2 block text-sm font-medium text-white/90">
                  Discount Percentage
                </label>
                <input
                  type="number"
                  value={addFormData.discount_percentage}
                  onChange={(e) => {
                    const value = e.target.value;
                    setAddFormData({
                      ...addFormData,
                      discount_percentage: value,
                    });
                    if (addFormErrors.discount_percentage) {
                      setAddFormErrors({
                        ...addFormErrors,
                        discount_percentage: "",
                      });
                    }
                  }}
                  className={`w-full rounded-md border ${
                    addFormErrors.discount_percentage
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                  } bg-navy-700 px-3 py-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400`}
                  placeholder="Add discount"
                  min="0"
                  max="100"
                />
                {addFormErrors.discount_percentage && (
                  <p className="mt-1 text-sm text-red-400">
                    {addFormErrors.discount_percentage}
                  </p>
                )}
              </div>

              {/* Duration Minutes */}
              <div>
                <label className="mb-2 block text-sm font-medium text-white/90">
                  Duration (Minutes)
                </label>
                <input
                  type="number"
                  value={addFormData.duration_minutes}
                  onChange={(e) => {
                    const value = e.target.value;
                    setAddFormData({
                      ...addFormData,
                      duration_minutes: value,
                    });
                    if (addFormErrors.duration_minutes) {
                      setAddFormErrors({
                        ...addFormErrors,
                        duration_minutes: "",
                      });
                    }
                  }}
                  className={`w-full rounded-md border ${
                    addFormErrors.duration_minutes
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                  } bg-navy-700 px-3 py-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400`}
                  placeholder="Add time"
                  min="1"
                />
                {addFormErrors.duration_minutes && (
                  <p className="mt-1 text-sm text-red-400">
                    {addFormErrors.duration_minutes}
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="mb-2 block text-sm font-medium text-white/90">
                  Description
                </label>
                <textarea
                  value={addFormData.description}
                  onChange={(e) => {
                    setAddFormData({
                      ...addFormData,
                      description: e.target.value,
                    });
                    if (addFormErrors.description) {
                      setAddFormErrors({
                        ...addFormErrors,
                        description: "",
                      });
                    }
                  }}
                  rows={1}
                  className={`w-full rounded-md border ${
                    addFormErrors.description
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                  } bg-navy-700 px-3 pt-2 pb-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 resize-none`}
                  placeholder="Enter package description"
                />
                {addFormErrors.description && (
                  <p className="mt-1 text-sm text-red-400">
                    {addFormErrors.description}
                  </p>
                )}
              </div>

              {/* Active Status */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-white/90">
                  Active Status
                </label>
                <Switch
                  checked={addFormData.is_active}
                  onChange={(e) => {
                    setAddFormData({
                      ...addFormData,
                      is_active: e.target.checked,
                    });
                  }}
                  color="purple"
                />
              </div>

              {/* Popular Status */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-white/90">
                  Popular
                </label>
                <Switch
                  checked={addFormData.is_popular}
                  onChange={(e) => {
                    setAddFormData({
                      ...addFormData,
                      is_popular: e.target.checked,
                    });
                  }}
                  color="purple"
                />
              </div>
              </div>

            {/* Buttons */}
            <div className="mt-6 flex justify-end gap-4">
              <button
                onClick={handleCloseAddModal}
                className="modal-btn rounded-md bg-gray-600 px-6 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleAddPackage}
                disabled={isLoading}
                className="modal-btn rounded-md bg-primary-gradient px-6 py-2 text-sm font-medium text-white/90 transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Creating..." : addButtonText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fade-in px-4"
          onClick={handleCloseDeleteModal}
        >
          <div
            className="w-full max-w-md rounded-xl bg-navy-800 p-6 shadow-lg animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="mb-4 text-xl font-bold text-white">Delete {viewType} Package</h2>
            
            <p className="mb-6 text-sm text-gray-300">
              Are you sure you want to delete this {viewType.toLowerCase()} package?
            </p>

            {/* Buttons */}
            <div className="flex justify-end gap-4">
              <button
                onClick={handleCloseDeleteModal}
                className="modal-btn rounded-md bg-gray-600 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="modal-btn rounded-md bg-primary-gradient px-6 py-2 text-sm font-medium text-white/90 transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audio Room Edit Order Modal */}
      {
        isAudioRoomEditOrderModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fade-in px-4"
            onClick={handleCloseAudioRoomEditOrderModal}
          >
            <div
              className="w-full max-w-lg rounded-xl bg-navy-800 p-6 shadow-lg animate-fade-in"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-white/90 mb-2">Reorder Audio Room Plans</h2>
                <p className="text-sm text-white/70">
                  Drag and drop audio room plans to reorder them. The order will be saved when you click 'Save Order'.
                </p>
              </div>

              {/* Modal Content - Sortable List */}
              {reorderAudioRoomData.length === 0 ? (
                <div className="py-8 text-center text-white/70">
                  No audio room plans found
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleAudioRoomReorderDragStart}
                  onDragOver={handleAudioRoomReorderDragOver}
                  onDragEnd={handleAudioRoomReorderDragEnd}
                >
                  <SortableContext
                    items={reorderAudioRoomData.map((item) => item.id || item._id || item.package_id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div 
                      className="space-y-2 mb-6 overflow-y-auto pr-2 reorder-modal-scroll"
                      style={{ 
                        maxHeight: '400px', // Approximately 5 items height
                      }}
                    >
                      {reorderAudioRoomData.map((item, index) => (
                        <SortableAudioRoomRow
                          key={item.id || item._id || item.package_id || `item-${index}`}
                          item={item}
                          index={index}
                          activeId={audioRoomActiveId}
                          overId={audioRoomOverId}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}

              {/* Modal Footer */}
              <div className="flex justify-end gap-4 pt-4 border-t border-gray-700">
                <button
                  onClick={handleCloseAudioRoomEditOrderModal}
                  disabled={isSavingAudioRoomOrder}
                  className="modal-btn rounded-md bg-gray-600 px-6 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAudioRoomOrder}
                  disabled={isSavingAudioRoomOrder || reorderAudioRoomData.length === 0}
                  className="modal-btn rounded-md bg-primary-gradient px-6 py-2 text-sm font-medium text-white/90 transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingAudioRoomOrder ? "Saving..." : "Save Order"}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Livestream Edit Order Modal */}
      {
        isLivestreamEditOrderModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fade-in px-4"
            onClick={handleCloseLivestreamEditOrderModal}
          >
            <div
              className="w-full max-w-lg rounded-xl bg-navy-800 p-6 shadow-lg animate-fade-in"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-white/90 mb-2">Reorder Live Stream Plans</h2>
                <p className="text-sm text-white/70">
                  Drag and drop live stream plans to reorder them. The order will be saved when you click 'Save Order'.
                </p>
              </div>

              {/* Modal Content - Sortable List */}
              {reorderLivestreamData.length === 0 ? (
                <div className="py-8 text-center text-white/70">
                  No live stream plans found
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleLivestreamReorderDragStart}
                  onDragOver={handleLivestreamReorderDragOver}
                  onDragEnd={handleLivestreamReorderDragEnd}
                >
                  <SortableContext
                    items={reorderLivestreamData.map((item) => item.id || item._id || item.package_id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div 
                      className="space-y-2 mb-6 overflow-y-auto pr-2 reorder-modal-scroll"
                      style={{ 
                        maxHeight: '400px', // Approximately 5 items height
                      }}
                    >
                      {reorderLivestreamData.map((item, index) => (
                        <SortableLivestreamRow
                          key={item.id || item._id || item.package_id || `item-${index}`}
                          item={item}
                          index={index}
                          activeId={activeId}
                          overId={overId}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}

              {/* Modal Footer */}
              <div className="flex justify-end gap-4 pt-4 border-t border-gray-700">
                <button
                  onClick={handleCloseLivestreamEditOrderModal}
                  disabled={isSavingLivestreamOrder}
                  className="modal-btn rounded-md bg-gray-600 px-6 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveLivestreamOrder}
                  disabled={isSavingLivestreamOrder || reorderLivestreamData.length === 0}
                  className="modal-btn rounded-md bg-primary-gradient px-6 py-2 text-sm font-medium text-white/90 transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingLivestreamOrder ? "Saving..." : "Save Order"}
                </button>
              </div>
            </div>
          </div>
        )
      }
    </div>
  );
};

export default AudioRoom;
